import { useState, useEffect, useRef } from 'react';

export default function Features() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: 'ri-customer-service-2-line',
      title: 'CRM Toàn diện',
      description: 'Quản lý tập trung khách hàng, bán hàng, marketing và chăm sóc khách hàng trong một hệ thống thống nhất',
      color: 'from-[#06b6d4] to-[#00c897]',
      items: [
        'Quản lý khách hàng tiềm năng',
        'Theo dõi cơ hội bán hàng',
        'Marketing automation',
        'Chăm sóc khách hàng 360°'
      ]
    },
    {
      icon: 'ri-folder-chart-line',
      title: 'Quản lý Dự án',
      description: 'Kết nối chặt chẽ với quản lý dự án – công việc theo từng khách hàng, hợp đồng hoặc deal',
      color: 'from-[#2a4b7c] to-[#3d5a80]',
      items: [
        'Theo dõi tiến độ dự án',
        'Quản lý nguồn lực',
        'Gắn công việc với khách hàng',
        'Deadline & milestone'
      ]
    },
    {
      icon: 'ri-flow-chart',
      title: 'Workflow Engine',
      description: 'Xây dựng và vận hành quy trình làm việc chuẩn hóa với phê duyệt nhiều cấp và SLA',
      color: 'from-[#2ecc71] to-[#00c897]',
      items: [
        'Quy trình tự động hóa',
        'Checklist theo phase',
        'Phê duyệt đa cấp',
        'Cảnh báo trễ tiến độ'
      ]
    }
  ];

  return (
    <section ref={sectionRef} id="features" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#06b6d4]/10 text-[#06b6d4] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <i className="ri-star-line"></i>
            <span>ĐIỂM KHÁC BIỆT NỔI BẬT</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            CRM + Project + Workflow
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Trong một nền tảng duy nhất - Khác với CRM truyền thống chỉ dừng ở bán hàng, 
            <strong className="text-[#2a4b7c]"> Stackway CRM kết nối toàn bộ vòng đời khách hàng</strong>
          </p>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#06b6d4]/30 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-4 sm:mb-6 group-hover:scale-110 transition-transform`}>
                <i className={`${feature.icon} text-2xl sm:text-2xl md:text-3xl text-white`}></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">{feature.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-5 md:mb-6 leading-relaxed">{feature.description}</p>
              <ul className="space-y-2 sm:space-y-3">
                {feature.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-check-line text-[#00c897] text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                    <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Workflow Visualization */}
        <div className={`bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-6 sm:mb-8 md:mb-12">
            Quản lý xuyên suốt vòng đời khách hàng
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 md:gap-4">
            {['Lead', 'Deal', 'Dự án', 'Công việc', 'Hợp đồng', 'Doanh thu'].map((step, index, array) => (
              <div key={index} className="flex items-center">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg border border-white/20 hover:bg-white/20 transition-all">
                  <span className="font-semibold text-sm sm:text-base md:text-lg">{step}</span>
                </div>
                {index < array.length - 1 && (
                  <i className="ri-arrow-right-line text-lg sm:text-xl md:text-2xl mx-1 sm:mx-2 text-[#00c897] hidden sm:block"></i>
                )}
              </div>
            ))}
          </div>
          <p className="text-center mt-4 sm:mt-6 md:mt-8 text-white/80 text-sm sm:text-base md:text-lg px-4">
            Giúp doanh nghiệp quản lý xuyên suốt từ giai đoạn tiếp cận khách hàng đến triển khai và hậu mãi
          </p>
        </div>
      </div>
    </section>
  );
}
